import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { Alert, Button } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import VisibilitySensor from '../../../../components/VisibilitySensor/VisibilitySensor';

import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';

import MessageView from './MessageView';

import { getConversationMessagesRequest, setConversationViewRequest, getConversation, canUserSeeMessage, getConversationParticipantId, CONVERSATION_STATUS_OPEN } from '../../ConversationActions';
import { getLoggedUser } from '../../../User/UserActions';

class MessagesList extends Component {
	constructor(props) {
		super(props);

		this.state = {
			isLightboxOpen: false,
			lightboxIndex: 0,
			isVisible: false,
			showHiddenCaptures: false,
		};

		this.messagesWrapper = React.createRef();
	}

	componentDidMount() {
		this.props.conversationId && this.props.dispatch(getConversationMessagesRequest(this.props.conversation._id)).then(() => {
			// this.handleConversationView();
			this.scrollToBottom();
		});
	}

	componentDidUpdate(prevProps, prevState) {
		// New message received
		if(prevProps.conversation && this.props.conversation && prevProps.conversation.messages && this.props.conversation.messages && prevProps.conversation.messages.length !== this.props.conversation.messages.length) {
			this.scrollToBottom();
			this.state.isVisible && this.handleConversationView();
		}
	}

	componentWillUnmount() {
		// this.handleConversationView();
	}

	getWrittingUsers() {
		return this.props.conversation && this.props.conversation.writingUsers ? this.props.conversation.writingUsers : [];
	}

	getMessages() {
		return ((this.props.conversation || {}).messages || [])
			.filter(message => canUserSeeMessage(message, this.props.user))
			.filter(message => !this.props.preserveAnonymity || !message.file || !message.file.includes('capture'))
		// Sub-minute burst captures are collapsed out of the chat (shown in the media
		// viewer instead), unless an authorized user explicitly reveals them.
			.filter(message => this.state.showHiddenCaptures || !message.data?.hidden)
			.sort((a, b) => {
				return new Date(a.date) - new Date(b.date);
			});
	}

	hasHiddenCaptures() {
		return ((this.props.conversation || {}).messages || []).some(message => message.data?.hidden && canUserSeeMessage(message, this.props.user));
	}

	getImages() {
		const wrapper = document.getElementById(`conversation-messages-wrapper-${this.props.conversation._id}`);
		const images = [];
		if(wrapper) {
			const imagesElements = wrapper.getElementsByTagName('img');
			for(let i = 0; i < imagesElements.length; i += 1) {
				images.push({
					src: imagesElements[i].src,
					alt: (imagesElements[i].getAttribute('app-filename') || '').split('/').pop(),
					filename: imagesElements[i].getAttribute('app-filename'), // usefull for setting current index
				});
			}
		}
		return images;
	}

	handleVisibilityChange = isVisible => {
		this.setState({
			isVisible,
		}, () => {
			if(this.state.isVisible) {
				this.handleConversationView();
			}
		});
	};

	handleConversationView() {
		if(this.props.conversation) {
			this.props.conversation.status === CONVERSATION_STATUS_OPEN
                && (this.props.user.role !== 'student' || this.props.conversation.participants.find(participant => getConversationParticipantId(participant) === this.props.user._id))
                && this.props.dispatch(setConversationViewRequest(this.props.conversation._id));
		}
	}

	handleToggleLightbox = file => {
		const index = this.getImages().reduce((i, image, index) => ((image.filename || '').includes(file) ? index : i), 0);
		console.log('Index', this.getImages(), file, index, this.getImages()[index]);
		this.setState({
			lightboxIndex: index,
			isLightboxOpen: !this.state.isLightboxOpen,
		});
	};

	/*
	 * Nearest ancestor actually scrolling the messages. It used to be the direct parent, until
	 * `VisibilitySensor` started wrapping this list in a div of its own: the scroll then landed
	 * on that div, which does not scroll, and the chat stayed stuck on its first messages.
	 * The search stops before the body, so opening a chat never moves the exam page.
	 */
	getScrollContainer() {
		let element = this.messagesWrapper.current?.parentElement;
		while(element && element !== document.body) {
			if(['auto', 'scroll'].includes(window.getComputedStyle(element).overflowY)) {
				return element;
			}
			element = element.parentElement;
		}
		return null;
	}

	scrollToBottom = () => {
		// Waits for the messages to be painted, their height being what is scrolled to.
		window.requestAnimationFrame(() => {
			const scrollContainer = this.getScrollContainer();
			if(scrollContainer) {
				scrollContainer.scroll({ top: scrollContainer.scrollHeight });
			}
		});
	};

	renderLightbox() {
		if(this.props.conversation) {
			const images = this.getImages();
			return (
				<Lightbox
					open={this.state.isLightboxOpen}
					close={this.handleToggleLightbox}
					slides={images.map(img => ({ src: img.src, alt: img.alt }))}
					index={this.state.lightboxIndex}
					on={{ view: ({ index }) => this.setState({ lightboxIndex: index }) }}
				/>
			);
		}
		return null;
	}

	render() {
		const messages = this.getMessages();
		// let previousWriter = null;
		let nextWriter = null;
		return (
			<VisibilitySensor partialVisibility onChange={this.handleVisibilityChange}>
				<div ref={this.messagesWrapper} className="conversation-messages-wrapper">
					{this.props.allowCaptureReveal && this.hasHiddenCaptures() && (
						<div className="text-center mb-1">
							<Button color="secondary" outline size="sm" onClick={() => this.setState({ showHiddenCaptures: !this.state.showHiddenCaptures })}>
								<FontAwesomeIcon icon={this.state.showHiddenCaptures ? 'eye-slash' : 'eye'} />{' '}
								{
									this.state.showHiddenCaptures
										? <FormattedMessage id="conversationHideCaptures" defaultMessage="Hide captures" />
										: <FormattedMessage id="conversationShowCaptures" defaultMessage="Show captures" />
								}
							</Button>
						</div>
					)}
					{
						this.props.conversation && this.props.conversation.messages && this.props.conversation.messages.length
							? (
								<div id={`conversation-messages-wrapper-${this.props.conversation._id}`} className="mt-2 conversation-messages-list">
									{messages.map((message, index) => {
										// let isSameWriter = false;
										// if(message.author && previousWriter === message.author._id) {
										//     isSameWriter = true;
										// }
										let displayAuthor = true;
										// previousWriter = message.author ? message.author._id : null;
										nextWriter = this.props.conversation.messages[index + 1] ? this.props.conversation.messages[index + 1].author : null;
										if(nextWriter && message.author && nextWriter._id === message.author._id) {
											displayAuthor = false;
										}
										return <MessageView key={message._id} message={message} displayAuthor={displayAuthor} openLightbox={this.handleToggleLightbox} addMessageDateLink={this.props.addMessageDateLink} />;
									})}
								</div>
							) : (
								<Alert color="warning" className="text-center">
									<FormattedMessage id="conversationNoMessages" defaultMessage="No messages for the moment..." />
								</Alert>
							)
					}
					{this.getWrittingUsers().map((user, index) => (
						<div key={index} className="p-2">
							<em><FormattedMessage id="conversationUserWritting" defaultMessage="{user} is writting..." values={{ user: `${user.firstName}` }} /></em>
						</div>
					))}
					{this.renderLightbox()}
				</div>
			</VisibilitySensor>
		);
	}
}

function mapStateToProps(store, props) {
	return {
		user: getLoggedUser(store),
		conversation: getConversation(store, props.conversationId),
	};
}

MessagesList.defaultProps = {
	maxHeight: 0,
	preserveAnonymity: false,
	isSilent: false,
};

MessagesList.propTypes = {
	dispatch: PropTypes.func.isRequired,
	user: PropTypes.object,
	conversation: PropTypes.object.isRequired,
	conversationId: PropTypes.string.isRequired,
	preserveAnonymity: PropTypes.bool,
	maxHeight: PropTypes.number,
	isSilent: PropTypes.bool,
	addMessageDateLink: PropTypes.bool,
	allowCaptureReveal: PropTypes.bool,
};

export default connect(mapStateToProps)(MessagesList);
